/**
 * Phase 6a.20e-hotfix — derive the calendar fiscal year close date
 * from an ISO YYYY-MM-DD audit period start.
 *
 * Mental model:
 *   • Engagement `auditPeriodStart` = engine period-end (e.g. "2025-01-01")
 *   • Auditor's fiscal year close   = one calendar day earlier (e.g. "2024-12-31")
 *
 * The engine wants period-end exactness; the auditor wants fiscal
 * year semantics. This helper bridges the two for display purposes.
 *
 * Returns the same date string in YYYY-MM-DD format, minus one
 * calendar day. Handles month/year rollover via JS Date arithmetic
 * (e.g. "2025-03-01" → "2025-02-28", "2024-03-01" → "2024-02-29").
 *
 * Returns null on malformed input — callers should treat null as
 * "cannot derive" and disable the feature rather than display
 * garbage.
 */
export function deriveFiscalYearClose(
  auditPeriodStart: string | undefined,
): string | null {
  if (!auditPeriodStart) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(auditPeriodStart);
  if (!match) return null;
  const [, y, m, d] = match;
  // UTC to avoid timezone-induced off-by-one (the user's locale must
  // not shift the date when we serialize back to YYYY-MM-DD).
  const utc = Date.UTC(Number(y), Number(m) - 1, Number(d));
  if (!Number.isFinite(utc)) return null;
  const prev = new Date(utc - 86_400_000);
  const yy = prev.getUTCFullYear();
  const mm = String(prev.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(prev.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
